import { Plus, Trash2 } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'

import type { GetPatientResponse } from '@snootsvet/shared'

import { FormError } from '@/frontend/components/form/primitives'
import { Button } from '@/frontend/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/frontend/components/ui/dialog'
import { PatientAvatar } from '@/frontend/features/patient/patient-avatar'
import { useUpdatePatient } from '@/frontend/hooks/api/patients/use-update-patient'
import { useUploadFile } from '@/frontend/hooks/use-upload-file'
import { resizeImageToJpeg } from '@/frontend/lib/images'

export function PatientPhotoDialog({
  patient,
  onClose,
}: {
  patient: Pick<GetPatientResponse['patient'], 'id' | 'name' | 'species' | 'photoUrl'>
  onClose: () => void
}) {
  const [file, setFile] = useState<File | null>(null)
  const [removed, setRemoved] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const { mutateAsync: uploadFile } = useUploadFile()
  const { mutateAsync: updatePatient } = useUpdatePatient(patient.id)

  const inputRef = useRef<HTMLInputElement>(null)
  const previewUrl = useMemo(() => (file ? URL.createObjectURL(file) : null), [file])
  useEffect(() => () => void (previewUrl && URL.revokeObjectURL(previewUrl)), [previewUrl])
  const shownUrl = previewUrl ?? (removed ? null : patient.photoUrl)

  async function save() {
    if (saving || (!file && !removed)) return
    setSaving(true)
    setError(null)
    try {
      if (file) {
        const resized = await resizeImageToJpeg(file, 1024)
        const uploaded = await uploadFile(resized)
        await updatePatient({ photo: { ...uploaded, contentType: 'image/jpeg' } })
      } else {
        await updatePatient({ photo: null })
      }
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save patient photo')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open onOpenChange={(open) => !open && !saving && onClose()} disablePointerDismissal>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>Edit Photo - {patient.name}</DialogTitle>
        </DialogHeader>
        <fieldset disabled={saving}>
          <div className="flex flex-col items-center gap-2 py-3">
            <div className="flex flex-col items-center gap-3">
              <PatientAvatar
                src={shownUrl}
                species={patient.species}
                name={patient.name}
                className="size-30 [&>svg]:size-14"
              />
              <div className="flex flex-col items-center">
                <Button type="button" variant="ghost" onClick={() => inputRef.current?.click()}>
                  <Plus className="mr-1.5 size-4" />
                  Upload photo
                </Button>
                {shownUrl && (
                  <Button
                    type="button"
                    variant="ghost-destructive"
                    onClick={() => {
                      setFile(null)
                      setRemoved(true)
                      setError(null)
                    }}
                  >
                    <Trash2 className="mr-1.5 size-4" />
                    Remove photo
                  </Button>
                )}
              </div>
              <input
                ref={inputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => {
                  const picked = e.target.files?.[0]
                  e.target.value = ''
                  if (!picked) return
                  if (!picked.type.startsWith('image/')) {
                    setError('Photo must be an image')
                    return
                  }
                  setFile(picked)
                  setRemoved(false)
                  setError(null)
                }}
              />
            </div>
            <FormError errors={error ? [error] : []} />
          </div>
        </fieldset>
        <DialogFooter>
          <Button variant="outline" size="lg" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button size="lg" onClick={save} disabled={saving || (!file && !removed)}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
